import { useMutation } from '@apollo/client/react';
import {
  type BookingTransactionDataInput,
  CreateBookingDocument,
  CreateBookingTransactionDocument,
} from '@drycut/graphql';
import { type Button } from '@drycut/ui-components';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { CheckoutButton } from './CheckoutButton';
import usePaymentMethod from './usePaymentCreation';

type Props = {
  amount: number;
  data: BookingTransactionDataInput;
} & React.ComponentProps<typeof Button>;

export function BookingCheckoutButton({ amount, data, ...props }: Props): React.ReactElement {
  const navigate = useNavigate();

  const [createPayment, { loading: creatingPayment }] = useMutation(
    CreateBookingTransactionDocument,
  );
  const [createBooking, { loading: creatingBooking }] = useMutation(CreateBookingDocument);

  const [transactionError, setTransactionError] = useState<string>();
  const [datatransId, setDatatransId] = useState<string>();

  const { paymentMethod, isPaymentSkipped } = usePaymentMethod({
    manual: data?.manual || false,
    amount,
  });

  const handleSubmit = async () => {
    const response = await createPayment({
      variables: { input: { amount, paymentMethod, data } },
    });

    const payload = response.data?.createBookingTransaction;

    if (payload?.errors && payload?.errors?.length > 0) {
      setTransactionError(payload.errors[0].message);
    } else {
      setTransactionError('');
      const transaction = payload?.paymentTransaction;

      // If it's a skip payment, we can create the booking right away and redirect the user to the
      // booking details when it's done. Otherwise the redirect will be a hard one from the
      // backend, after Datatrans has done its thing.
      if (payload && isPaymentSkipped && transaction?.id) {
        const booking = await createBooking({
          variables: { input: { transactionId: transaction?.id } },
        });

        const response = booking.data?.createBooking;
        const uid = response?.booking?.uid;
        const errors = response?.errors;

        if (errors && errors.length > 0) {
          setTransactionError(errors[0].message);
          return;
        }

        navigate(`/booking/${uid}`);
      }

      if (transaction?.datatransId) {
        setDatatransId(transaction.datatransId);
      }
    }
  };

  const loading = creatingPayment || creatingBooking || !!datatransId;

  return (
    <CheckoutButton
      manual={Boolean(data?.manual)}
      onClick={handleSubmit}
      onCancel={() => setDatatransId('')}
      loading={loading}
      datatransId={datatransId}
      transactionError={transactionError}
      onErrorClose={() => setTransactionError('')}
      {...props}
    />
  );
}
